/**
 * [LGP 2026-10-15, user] NEW FILE — Lead Generator Performance → Bonus Rules: the Apps Script page "Bonus Rules" (Index.html
 * data-page="bonus-rules"; JavaScript.html loadBonus, onBonus, bandLabel, bandNote, renderBonus, editBonus, addBonusRule,
 * removeBonusRule, previewBonus, saveBonus, resetBonus). Rules are edited here and saved as a SET (an overlap is only visible
 * across rows); the server checks them again before storing anything (lead_generator_settings). Anyone with this tab may edit
 * them (user: "admin only and the person having the access for this tab").
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { SkeletonBox } from '../../components/Skeleton';
import { lgSunken } from '../../styles/liquidGlass'; // [LIQUID-GLASS 2026-11-02]
import { fetchLgpBonus, LgpBonusRules, resetLgpBonus, saveLgpBonus } from '../../services/api';
import {
  ViewWrap, ViewBar, PrimaryBtn, SecondaryBtn, Toolbar, StatusLine, TableScroll, dark, fmtBonus, errorText, ViewProps,
} from './lgpShared';

type Draft = { from: string; to: string; amount: string };
/** [JavaScript.html boundText] An optional bound as text ('' for none). */
const boundText = (v: unknown) => (v === null || v === undefined ? '' : String(v).trim());
/** [JavaScript.html bandLabel] '70 – 79' / '100 and above' / a dash while a row is incomplete. */
function bandLabel(r: Draft): string {
  const from = boundText(r.from); const to = boundText(r.to);
  if (from === '') return '—';
  if (to === '') return `${from} and above`;
  return `${from} – ${to}`;
}
/** [JavaScript.html bandNote] A rule paying 0 is valid but shows as a dash on Leads Count — say so plainly. */
function bandNote(r: Draft): string {
  const amount = boundText(r.amount);
  if (amount === '') return 'needs an amount';
  if (Number(amount) === 0) return 'pays nothing';
  return '';
}

/** [LGP-BONUS-ADD 2026-10-15] A bound as a whole number, or null when it is blank / not a number. */
const wholeOf = (v: string): number | null => { const t = boundText(v); if (t === '') return null; const n = Number(t); return Number.isFinite(n) ? n : null; };
const describeBand = (from: number, to: number | null) => (to === null ? `${from}+` : `${from}–${to}`);

/** [LGP-BONUS-ADD 2026-10-15] The server's own checks ([Bonus.js validateBonusRules_], services/leadGenPerformance.ts — the same
 *  words), run before anything is sent so the row at fault can be marked; the server still checks again before storing. */
function checkDraft(draft: Draft[], maxRules: number): { text: string; rows: number[] } | null {
  if (draft.length > maxRules) return { text: `Too many rules — the maximum is ${maxRules}.`, rows: [] };
  const clean: { i: number; from: number; to: number | null }[] = [];
  for (let i = 0; i < draft.length; i++) {
    const label = `Rule ${i + 1}`; const r = draft[i];
    const field = (v: string, name: string): number | string => {
      const t = boundText(v); const n = Number(t);
      if (t === '' || !Number.isFinite(n)) return `${label}: "${name}" must be a number.`;
      if (Math.floor(n) !== n) return `${label}: "${name}" must be a whole number.`;
      return n;
    };
    const from = field(r.from, 'From');
    if (typeof from === 'string') return { text: from, rows: [i] };
    let to: number | null = null;
    if (boundText(r.to) !== '') { const t = field(r.to, 'To'); if (typeof t === 'string') return { text: t, rows: [i] }; to = t; }
    const amount = field(r.amount, 'Bonus');
    if (typeof amount === 'string') return { text: amount, rows: [i] };
    if (from < 0) return { text: `${label}: "From" cannot be negative.`, rows: [i] };
    if (amount < 0) return { text: `${label}: "Bonus" cannot be negative.`, rows: [i] };
    if (to !== null && to < from) return { text: `${label}: "To" (${to}) is lower than "From" (${from}).`, rows: [i] };
    clean.push({ i, from, to });
  }
  clean.sort((a, b) => a.from - b.from);
  const open = clean.filter((r) => r.to === null);
  if (open.length > 1) return { text: 'Only one rule can be left open ended ("and above").', rows: open.map((r) => r.i) };
  if (open.length === 1 && clean[clean.length - 1].to !== null) return { text: 'The open-ended rule must be the highest band.', rows: [open[0].i] };
  for (let k = 0; k < clean.length - 1; k++) {
    const a = clean[k]; const b = clean[k + 1];
    if ((a.to === null ? Infinity : a.to) >= b.from) {
      return { text: `Rules overlap: ${describeBand(a.from, a.to)} and ${describeBand(b.from, b.to)} both cover ${b.from}.`, rows: [a.i, b.i] };
    }
  }
  return null;
}

/* [LGP-WIDE 2026-10-15, user] "in the bonus rule make the row to the screen complete, right now it is showing half": the table
   runs the full width (it was capped at 820 px); fixed column shares, the boxes fill their columns like a sheet's cells. */
const Table = styled.table`
  border-collapse: separate;
  border-spacing: 0;
  width: 100%;
  table-layout: fixed;
  font-size: 13px;

  th {
    position: sticky;
    top: 0;
    z-index: 1;
    padding: 9px 12px;
    font-size: 11px;
    font-weight: 800;
    letter-spacing: 0.3px;
    text-transform: uppercase;
    text-align: left;
    color: ${(p) => p.theme.greenInk};
    background: ${(p) => (dark(p) ? '#262827' : '#eef0ef')};
    border-bottom: 1px solid ${(p) => p.theme.line};
  }
  th.num, td.num { text-align: right; }
  td { padding: 8px 12px; border-bottom: 1px solid ${(p) => p.theme.line}; color: ${(p) => p.theme.text}; }
  td.band { font-weight: 700; white-space: nowrap; }
  .band-note { margin-left: 8px; font-size: 11.5px; font-weight: 700; color: ${(p) => p.theme.danger}; }
  tr.msg td { padding: 26px 12px; text-align: center; color: ${(p) => p.theme.dim}; }
  /* [LIQUID-GLASS 2026-11-02, user] the boxes are pressed into the glass; a soft green ring and glow while typing */
  input {
    width: 100%;
    height: 32px;
    padding: 0 10px;
    border-radius: 8px;
    ${lgSunken}
    color: ${(p) => p.theme.text};
    font: inherit;
    font-size: 13px;
    text-align: right;
    outline: none;
  }
  input::placeholder { color: ${(p) => p.theme.dim}; }
  .remove { border: none; background: transparent; color: ${(p) => p.theme.danger}; font: inherit; font-size: 12.5px; font-weight: 700; cursor: pointer; }
  .remove:disabled { opacity: 0.5; cursor: not-allowed; }
  /* [LGP-BONUS-ADD] the row a Save was stopped for, until it is edited */
  tr.bad td { background: ${(p) => (dark(p) ? 'rgba(248,113,113,0.10)' : 'rgba(220,38,38,0.06)')}; }
  tr.bad input { border-color: ${(p) => p.theme.danger}; }

  /* [PHONE-LAYOUT 2026-10-23] phones: the laptop's percentage columns cut the boxes (an amount of 1000 showed as 100, the
     "and above" hint and Remove were cut, "90 and above" ran into the next box). Here every column takes the width its
     contents need — each box just fits its value (From up to 3 digits, the "and above" hint, a 5-digit Bonus), the headings
     wrap, the cells are tighter — so the table fills the phone's line and, when its cells need more, swipes sideways in its
     box (about 450 px). Remove gets a finger-sized tap area. */
  @media (max-width: 768px) {
    table-layout: auto;
    col { width: auto !important; }
    th, td { padding-left: 6px; padding-right: 6px; }
    th { white-space: normal; }
    td { white-space: nowrap; }
    input { width: 72px; padding: 0 8px; }
    input[data-bonus-field='to'] { width: 100px; }
    input[data-bonus-field='amount'] { width: 80px; }
    .remove { padding: 9px 4px; }
  }
`;
const Try = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  label { font-size: 12.5px; font-weight: 700; color: ${(p) => p.theme.dim}; }
  input {
    width: 110px;
    height: 34px;
    padding: 0 10px;
    border-radius: 10px;
    ${lgSunken} /* [LIQUID-GLASS 2026-11-02] pressed in */
    color: ${(p) => p.theme.text};
    font: inherit;
    outline: none;
  }
  .out { font-size: 14px; font-weight: 800; color: ${(p) => p.theme.dim}; }
  .out.earned { color: ${(p) => p.theme.greenInk}; }
`;

const BonusView: React.FC<ViewProps & { onPatchBonus: (rules: { from: unknown; to: unknown; amount: unknown }[]) => void }> = ({ active, noDock, onVersion, register, onPatchBonus }) => {
  const [data, setData] = useState<LgpBonusRules | null>(null);
  const [draft, setDraft] = useState<Draft[]>([]);
  const [note, setNote] = useState<{ text: string; error?: boolean }>({ text: 'Reading rules…' });
  const [busy, setBusy] = useState(false);
  const [tryValue, setTryValue] = useState('');
  const [bad, setBad] = useState<number[]>([]);           // [LGP-BONUS-ADD] rows a Save was stopped for
  const req = useRef(0);
  const tableRef = useRef<HTMLTableElement>(null);
  const focusAmount = useRef<number | null>(null);        // the row whose Bonus box takes the cursor after the next draw

  /** [JavaScript.html onBonus] */
  const onBonus = useCallback((d: LgpBonusRules) => {
    setData(d);
    const next = d.rules.map((r) => ({ from: boundText(r.from), to: boundText(r.to), amount: boundText(r.amount) }));
    setDraft(next);
    setNote({ text: `${next.length} ${next.length === 1 ? 'rule' : 'rules'}${d.isDefault ? ' · using the built-in defaults' : ' · saved'}` });
    onVersion(d.version, 'bonus-rules');
  }, [onVersion]);

  /** [JavaScript.html loadBonus] */
  const load = useCallback((silent: boolean) => {
    if (!silent) { setNote({ text: 'Reading rules…' }); setData(null); }
    const n = ++req.current;
    fetchLgpBonus()
      .then((d) => { if (n === req.current) onBonus(d); })
      .catch((err) => { if (silent || n !== req.current) return; setNote({ text: errorText(err), error: true }); });
  }, [onBonus]);
  const loadRef = useRef(load);
  loadRef.current = load;

  const inited = useRef(false);
  useEffect(() => {
    if (!active || inited.current) return;
    inited.current = true;
    loadRef.current(false);
    register('bonus-rules', { refresh: () => loadRef.current(true) });
  }, [active, register]);

  const edit = (i: number, key: keyof Draft, value: string) => {
    setDraft((d) => d.map((r, j) => (j === i ? { ...r, [key]: value } : r)));
    if (bad.length) setBad([]);
  };

  // [LGP-BONUS-ADD] the new rule's Bonus box takes the cursor as soon as the row is drawn
  useEffect(() => {
    const i = focusAmount.current;
    if (i === null) return;
    focusAmount.current = null;
    tableRef.current?.querySelector<HTMLInputElement>(`[data-bonus-row="${i}"] [data-bonus-field="amount"]`)?.focus();
  }, [draft]);

  /** [JavaScript.html addBonusRule] The new band starts just above the highest one.
   *  [LGP-BONUS-ADD 2026-10-15, user] "in the bonus rule add should work good, new bonus rule can be added and remove": the
   *  script's way left the set unsaveable whenever the top band was open ended — from the defaults it added "91 and above" under
   *  "90 and above", which both cover 91, so every Save was refused until the 90 band was closed by hand. Now that top band is
   *  closed at the bands' own step (the band below it: 80–89 → 10 wide, so 90 and above → 90–99) and the new band opens right
   *  above it (100 and above): with its amount typed in, the set saves as it is. The cursor goes to the new Bonus box. */
  const add = () => {
    if (data && draft.length >= data.maxRules) { setNote({ text: `Maximum of ${data.maxRules} rules.`, error: true }); return; }
    const bands = draft.map((r, i) => ({ i, from: wholeOf(r.from), to: boundText(r.to) === '' ? null : wholeOf(r.to) }))
      .filter((b): b is { i: number; from: number; to: number | null } => b.from !== null)
      .sort((a, b) => a.from - b.from);
    const next = draft.slice();
    const topBand = bands[bands.length - 1];
    let from = 70;
    let closed = '';
    if (topBand && topBand.to === null) {
      const below = bands[bands.length - 2];
      const step = below && below.to !== null && below.to >= below.from ? below.to - below.from + 1 : 10;
      const end = topBand.from + step - 1;
      next[topBand.i] = { ...next[topBand.i], to: String(end) };
      closed = `${topBand.from} and above is now ${topBand.from} – ${end}`;
      from = end + 1;
    } else if (bands.length) {
      from = Math.max(...bands.map((b) => (b.to === null ? b.from : Math.max(b.from, b.to)))) + 1;
    }
    next.push({ from: String(from), to: '', amount: '' });
    focusAmount.current = next.length - 1;
    setDraft(next);
    setBad([]);
    setNote({ text: `Rule added: ${from} and above${closed ? ` (${closed})` : ''} — enter its bonus amount, then Save changes.` });
  };
  /** [JavaScript.html removeBonusRule] */
  const remove = (i: number) => {
    setDraft((d) => d.filter((_, j) => j !== i));
    setBad([]);
    setNote({ text: 'Rule removed — Save changes to apply.' });
  };

  /** [JavaScript.html previewBonus] Low band first, exactly as the server evaluates — the preview never disagrees. */
  const preview = (() => {
    if (tryValue === '' || Number.isNaN(Number(tryValue))) return { text: '—', earned: false };
    const n = Number(tryValue);
    const bands = draft
      .map((r) => {
        const from = boundText(r.from); const to = boundText(r.to);
        return { from: from === '' ? NaN : Number(from), to: to === '' ? null : Number(to), amount: Number(boundText(r.amount)) || 0 };
      })
      .filter((b) => Number.isFinite(b.from))
      .sort((a, b) => a.from - b.from);
    let amount = 0;
    for (const b of bands) { if (n >= b.from && (b.to === null || n <= b.to)) { amount = b.amount; break; } }
    return { text: amount ? fmtBonus(amount) : 'No bonus', earned: !!amount };
  })();

  /** [JavaScript.html saveBonus] [LGP-BONUS-ADD] a set the server would refuse is stopped here, its row marked. */
  const save = () => {
    const problem = checkDraft(draft, data?.maxRules ?? 12);
    if (problem) { setBad(problem.rows); setNote({ text: problem.text, error: true }); return; }
    setBad([]);
    ++req.current;
    setBusy(true);
    setNote({ text: 'Saving…' });
    saveLgpBonus(draft)
      .then((d) => {
        ++req.current;
        setBusy(false);
        onBonus(d);
        onPatchBonus(d.rules);                          // …Leads Count shows the new bonuses now
        const zero = (d.rules || []).filter((r) => Number(r.amount) === 0);
        setNote({ text: `Saved · Leads Count now uses these rules.${zero.length ? `  Note: ${zero.length} ${zero.length === 1 ? 'rule pays' : 'rules pay'} ₹0, which shows as a dash.` : ''}` });
      })
      .catch((err) => { setBusy(false); setNote({ text: errorText(err), error: true }); });
  };
  /** [JavaScript.html resetBonus] */
  const reset = () => {
    ++req.current;
    setBusy(true);
    setNote({ text: 'Restoring defaults…' });
    resetLgpBonus()
      .then((d) => { ++req.current; setBusy(false); onBonus(d); onPatchBonus(d.rules); setNote({ text: 'Defaults restored.' }); })
      .catch((err) => { setBusy(false); setNote({ text: errorText(err), error: true }); });
  };

  return (
    <ViewWrap data-lgp-view="bonus-rules">
      <ViewBar>
        <span className="sub">Bonus rules · applied to each person’s Final Count on Leads Count</span>
        <div className="controls">
          <SecondaryBtn type="button" disabled={busy || !data} onClick={add} data-bonus="add">+ Add rule</SecondaryBtn>
          <SecondaryBtn type="button" disabled={busy || !data} onClick={reset} data-bonus="reset">Reset to default</SecondaryBtn>
          <PrimaryBtn type="button" disabled={busy || !data} onClick={save} data-bonus="save">Save changes</PrimaryBtn>
        </div>
      </ViewBar>
      <Toolbar>
        <Try>
          <label htmlFor="lgp-bonus-try">Try a Final Count</label>
          <input id="lgp-bonus-try" type="number" min={0} step={1} placeholder="e.g. 95" value={tryValue} onChange={(e) => setTryValue(e.target.value)} data-bonus-try="" />
          <span className={`out${preview.earned ? ' earned' : ''}`} data-bonus-try-out="">{preview.text}</span>
        </Try>
        <StatusLine $error={!!note.error} data-status="">{note.text}</StatusLine>
      </Toolbar>
      <TableScroll $noDock={noDock}>
        <Table data-bonus-table="" ref={tableRef}>
          <colgroup><col style={{ width: '22%' }} /><col style={{ width: '22%' }} /><col style={{ width: '22%' }} /><col style={{ width: '22%' }} /><col style={{ width: '12%' }} /></colgroup>
          <thead>
            <tr><th>Band</th><th className="num">From (Final Count)</th><th className="num">To</th><th className="num">Bonus (₹)</th><th className="num">&nbsp;</th></tr>
          </thead>
          <tbody>
            {!data ? (note.error ? <tr className="msg"><td colSpan={5}>{note.text}</td></tr> : [0, 1, 2].map((i) => (
              <tr key={i}>
                <td><SkeletonBox $w="70%" $h="12px" /></td>
                <td className="num"><SkeletonBox $w="46px" $h="12px" $align="right" /></td>
                <td className="num"><SkeletonBox $w="46px" $h="12px" $align="right" /></td>
                <td className="num"><SkeletonBox $w="60px" $h="12px" $align="right" /></td>
                <td className="num"><SkeletonBox $w="52px" $h="12px" $align="right" /></td>
              </tr>
            ))) : !draft.length ? (
              <tr className="msg"><td colSpan={5}>No bonus rules — nobody earns a bonus. Use “+ Add rule” to create one.</td></tr>
            ) : draft.map((r, i) => (
              // eslint-friendly key: a rule has no id; its place is its identity while editing
              <tr key={`rule-${i}`} data-bonus-row={i} className={bad.includes(i) ? 'bad' : undefined}>
                <td className="band">{bandLabel(r)}{bandNote(r) && <span className="band-note">{bandNote(r)}</span>}</td>
                <td className="num"><input type="number" min={0} step={1} value={r.from} disabled={busy} onChange={(e) => edit(i, 'from', e.target.value)} data-bonus-field="from" /></td>
                <td className="num"><input type="number" min={0} step={1} placeholder="and above" value={r.to} disabled={busy} onChange={(e) => edit(i, 'to', e.target.value)} data-bonus-field="to" /></td>
                <td className="num"><input type="number" min={0} step={100} value={r.amount} disabled={busy} onChange={(e) => edit(i, 'amount', e.target.value)} data-bonus-field="amount" /></td>
                <td className="num"><button type="button" className="remove" disabled={busy} onClick={() => remove(i)}>Remove</button></td>
              </tr>
            ))}
          </tbody>
        </Table>
      </TableScroll>
    </ViewWrap>
  );
};

export default BonusView;
